import React from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Typography } from "../../Components/Typography";
import Card from "../../Components/Card";
import Button from "../../Components/Button";
import { isPartnerLikeRole } from "./utils";

const CompletionScreen = ({ completionType, user, onNavigate }) => {
  const navigate = useNavigate();
  const isPending = completionType !== "approved";

  const handleGetStarted = () => {
    if (onNavigate) {
      onNavigate(user?.role === "mentor" ? "/mentor-dashboard" : "/");
    } else {
      navigate(user?.role === "mentor" ? "/mentor-dashboard" : "/");
    }
  };

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary px-8 py-8">
      <div className="flex items-center justify-between w-full">
        <Typography as="h1" className="text-2xl font-bold text-primary-900">
          Registration Complete
        </Typography>
      </div>

      <Card className="w-full overflow-hidden p-0 max-w-3xl mx-auto">
        <div className="bg-slate-800 px-6 py-8 md:py-10 text-center relative">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-emerald-400 flex items-center justify-center text-white">
              <Icon icon="lucide:check" className="w-7 h-7" strokeWidth={3} />
            </div>
          </div>
          <Typography as="p" className="text-sm font-semibold uppercase tracking-wider text-emerald-400 mb-2">
            SUCCESS
          </Typography>
          <Typography as="h2" className="text-xl md:text-2xl font-bold text-white mb-3">
            Together we are building a world-class innovation ecosystem
          </Typography>
          <Typography as="p" className="text-sm text-slate-200 max-w-xl mx-auto">
            {isPending ? (
              <>
                Thank you for your interest in joining the <strong className="text-white">IgniteNext</strong> community. We are honored to have your expertise on board.
              </>
            ) : user?.role === "mentor" ? (
              <>Thank you for completing your registration. You can now explore the <strong className="text-white">IgniteNext</strong> platform.</>
            ) : isPartnerLikeRole(user?.role) ? (
              <>Thank you for completing your registration. We are looking forward to partnering with you at <strong className="text-white">IgniteNext</strong>.</>
            ) : user?.role === "investor" ? (
              <>Thank you for completing your registration. We are looking forward to working with you at <strong className="text-white">IgniteNext</strong>.</>
            ) : (
              <>Thank you for your interest in joining the <strong className="text-white">IgniteNext</strong> community. We are looking forward to speaking to you soon.</>
            )}
          </Typography>
        </div>

        <div className="px-6 py-8 md:py-10 bg-white">
          <Typography as="h3" className="text-lg font-bold text-primary-900 text-center mb-1">
            What happens next?
          </Typography>
          <div className="w-12 h-0.5 bg-violet-500 mx-auto mb-8" aria-hidden />

          {isPending ? (
            <>
              <div className="space-y-6 max-w-xl mx-auto">
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Icon icon="lucide:users" className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <Typography as="p" className="font-semibold text-primary-900">Discussion with Board</Typography>
                      <Typography as="p" className="text-sm text-tertiary-600 mt-0.5">
                        Our leadership board reviews your profile to align your expertise with our current ecosystem needs.
                      </Typography>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium shrink-0 w-fit">
                      2-3 Days
                    </span>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Icon icon="lucide:shield-check" className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <Typography as="p" className="font-semibold text-primary-900">Validation</Typography>
                      <Typography as="p" className="text-sm text-tertiary-600 mt-0.5">
                        Once verified, we finalize your credentials and prepare your unique mentor profile.
                      </Typography>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium shrink-0 w-fit">
                      Final Step
                    </span>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Icon icon="lucide:mail" className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <Typography as="p" className="font-semibold text-primary-900">Welcome Access</Typography>
                      <Typography as="p" className="text-sm text-tertiary-600 mt-0.5">
                        Watch your inbox for your welcome email containing secure access links to the IgniteNext Portal.
                      </Typography>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium shrink-0 w-fit">
                      Instant on Approval
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-tertiary-600 text-center italic mt-10 max-w-md mx-auto">
                &ldquo;Great things in business are never done by one person; they&rsquo;re done by a team of people.&rdquo;
              </p>
            </>
          ) : (
            <div className="flex flex-col items-center gap-6">
              <Typography as="p" className="text-base text-tertiary-600 text-center max-w-md">
                {user?.role === "mentor"
                  ? "Your profile is verified. Use the button below to access your dashboard."
                  : "You&rsquo;re all set. Use the button below to go to your dashboard."}
              </Typography>
              <Button
                variant="primary"
                size="large"
                onClick={handleGetStarted}
                startIcon={<Icon icon="lucide:arrow-right" className="h-5 w-5" />}
              >
                {user?.role === "mentor" ? "Get Started" : "Go to Dashboard"}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CompletionScreen;
